import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { designPx, REVIEW_GRID_COLUMNS } from './aiReviewScale';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { baseColors } from '../../../../Themes/colors';
import { ReviewTooltip, SOURCE_DOCUMENT_LABELS } from './OverviewFieldMatchSection';
export var REVIEW_COLORS = {
    white: '#FFFFFF',
    gray50: '#F9FAFB',
    gray100: '#F3F4F6',
    gray200: '#E5E7EB',
    gray400: '#9CA3AF',
    gray500: '#6B7280',
    gray700: '#374151',
    gray800: '#1F2937',
    teal50: '#F0FDFA',
    teal100: '#CCFBF1',
    teal200: '#99F6E4',
    teal500: '#14B8A6',
    teal600: '#0D9488',
    teal700: '#0F766E',
    amber50: '#FFFBEB',
    amber200: '#FDE68A',
    amber400: '#FBBF24',
    amber500: '#F59E0B',
    amber700: '#B45309',
    emerald50: '#ECFDF5',
    emerald200: '#A7F3D0',
    emerald400: '#34D399',
    emerald500: '#10B981',
    emerald700: '#047857',
    reference: '#D7F5EC',
    matchedBg: baseColors.successPreset[60],
    matchedBorder: baseColors.successPreset[70],
    matchedIcon: baseColors.successPreset[40],
    matchedText: baseColors.successPreset[50],
    reviewBg: baseColors.greyPreset[55],
    reviewBorder: baseColors.greyPreset[40],
    reviewIcon: baseColors.errorPreset[10],
    reviewText: baseColors.errorPreset[20],
};
export var SourceDocumentIcon = function (_a) {
    var label = _a.label;
    if (label === SOURCE_DOCUMENT_LABELS.invoice)
        return React.createElement(ReceiptLongOutlinedIcon, null);
    if (label === SOURCE_DOCUMENT_LABELS.driversLicense)
        return React.createElement(BadgeOutlinedIcon, null);
    return React.createElement(DescriptionOutlinedIcon, null);
};
export var OVERLINE_SX = {
    fontSize: designPx(10),
    lineHeight: 1.5,
    fontWeight: 400,
    letterSpacing: '0.025em',
    textTransform: 'uppercase',
    color: REVIEW_COLORS.gray400,
};
export var SquareIcon = function (_a) {
    var size = _a.size, radius = _a.radius, iconSize = _a.iconSize, _b = _a.tinted, tinted = _b === void 0 ? false : _b, _c = _a.plain, plain = _c === void 0 ? false : _c, children = _a.children;
    var color = REVIEW_COLORS.gray400;
    if (tinted)
        color = baseColors.successPreset[30];
    if (plain)
        color = REVIEW_COLORS.gray500;
    return (React.createElement(Box, { sx: {
            width: designPx(size),
            height: designPx(size),
            borderRadius: designPx(radius),
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tinted ? REVIEW_COLORS.reference : plain ? REVIEW_COLORS.gray100 : REVIEW_COLORS.white,
            border: plain || tinted ? 'none' : "1px solid ".concat(REVIEW_COLORS.gray200),
            color: color,
            '& svg': { fontSize: designPx(iconSize), color: 'inherit' },
            '& span': { fontSize: designPx(iconSize), color: 'inherit' },
        } }, children));
};
export var MissingIcon = function (_a) {
    var _b = _a.title, title = _b === void 0 ? 'Missing Info' : _b;
    return (React.createElement(Tooltip, { title: title, placement: "top", arrow: true, disableInteractive: true },
        React.createElement(Box, { sx: {
                width: designPx(24),
                height: designPx(24),
                borderRadius: designPx(6),
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: "1px solid ".concat(REVIEW_COLORS.amber200),
                backgroundColor: REVIEW_COLORS.amber50,
                color: REVIEW_COLORS.amber700,
            } },
            React.createElement(WarningAmberOutlinedIcon, { sx: { fontSize: designPx(14), color: 'inherit' } }))));
};
export var RuleTooltipContent = function (_a) {
    var reason = _a.reason, columns = _a.columns;
    return (React.createElement(Box, null,
        reason && (React.createElement(Box, { sx: { maxWidth: designPx(360), mb: columns.length > 0 ? designPx(8) : 0 } },
            React.createElement(Typography, { sx: { fontSize: designPx(10), lineHeight: 1.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)' } }, "AI Reason"),
            React.createElement(Typography, { sx: { fontSize: designPx(12), lineHeight: designPx(16), fontWeight: 500, color: baseColors.fullWhite } }, reason))),
        columns.length > 0 && (React.createElement(Box, { sx: {
                display: 'grid',
                gridTemplateColumns: "repeat(".concat(columns.length, ", max-content)"),
                columnGap: designPx(20),
                rowGap: designPx(2),
            } },
            columns.map(function (column) { return (React.createElement(Typography, { key: "".concat(column.header, "-header"), sx: { fontSize: designPx(10), lineHeight: 1.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', whiteSpace: 'nowrap' } }, column.header)); }),
            columns.map(function (column) {
                var hasData = Boolean(column.value) && column.value !== '--';
                return (React.createElement(Typography, { key: "".concat(column.header, "-value"), sx: {
                        fontSize: designPx(12),
                        lineHeight: designPx(16),
                        fontWeight: 500,
                        color: baseColors.fullWhite,
                        maxWidth: designPx(220),
                        wordBreak: 'break-word',
                        textAlign: hasData ? 'left' : 'center',
                    } }, hasData ? column.value : '--'));
            })))));
};
var IndicatorLine = function (_a) {
    var indicator = _a.indicator;
    var statusOk = indicator.matched || Boolean(indicator.valueWarning);
    var color = statusOk ? REVIEW_COLORS.emerald700 : REVIEW_COLORS.amber700;
    var header = indicator.documentLabel === SOURCE_DOCUMENT_LABELS.documentPackage ? 'Reference' : indicator.documentLabel;
    var line = (React.createElement(Stack, { direction: "row", alignItems: "center", sx: { gap: designPx(4), minWidth: 0 } },
        indicator.prefix && (React.createElement(Typography, { sx: { fontSize: designPx(11), lineHeight: 1.5, fontWeight: 500, color: REVIEW_COLORS.gray500, whiteSpace: 'nowrap' } }, "".concat(indicator.prefix, ":"))),
        indicator.documentLabel === SOURCE_DOCUMENT_LABELS.documentPackage && (statusOk
            ? React.createElement(CheckCircleIcon, { sx: { fontSize: designPx(13), color: REVIEW_COLORS.emerald500, flexShrink: 0 } })
            : React.createElement(CancelIcon, { sx: { fontSize: designPx(13), color: REVIEW_COLORS.amber500, flexShrink: 0 } })),
        React.createElement(Typography, { noWrap: true, sx: { fontSize: designPx(11), lineHeight: 1.5, fontWeight: 500, color: color, minWidth: 0 } }, indicator.value && indicator.valueWarning ? (React.createElement(React.Fragment, null, "".concat(indicator.title, " | "),
            React.createElement(Box, { component: "span", sx: { color: REVIEW_COLORS.amber700, display: 'inline-flex', alignItems: 'center', gap: designPx(3), verticalAlign: 'bottom' } },
                React.createElement(WarningAmberRoundedIcon, { sx: { fontSize: designPx(13) } }),
                indicator.value))) : indicator.value ? "".concat(indicator.title, " | ").concat(indicator.value) : indicator.title)));
    return (React.createElement(ReviewTooltip, { title: React.createElement(RuleTooltipContent, { reason: indicator.reason, columns: [{ header: header, value: indicator.valueWarning ? undefined : indicator.value }] }) }, line));
};
var OverviewSourceDocuments = function (_a) {
    var documents = _a.documents, _b = _a.indicators, indicators = _b === void 0 ? [] : _b;
    if (documents.length === 0)
        return null;
    var unplacedIndicators = indicators.filter(function (indicator) { return !documents.some(function (doc) { return doc.label === indicator.documentLabel; }); });
    return (React.createElement(Box, { sx: {
            backgroundColor: REVIEW_COLORS.white,
            border: "1px solid ".concat(REVIEW_COLORS.gray200),
            borderRadius: designPx(12),
            px: designPx(12),
            py: designPx(8),
        } },
        React.createElement(Stack, { direction: "row", alignItems: "center", flexWrap: "wrap", sx: { columnGap: designPx(8), rowGap: designPx(4), mb: designPx(10) } },
            React.createElement(SquareIcon, { size: 24, radius: 6, iconSize: 14, plain: true },
                React.createElement(FolderOpenOutlinedIcon, null)),
            React.createElement(Typography, { sx: { fontSize: designPx(14), lineHeight: designPx(20), fontWeight: 700, color: REVIEW_COLORS.gray800 } }, "Source Documents")),
        React.createElement(Box, { sx: {
                display: 'grid',
                gridTemplateColumns: REVIEW_GRID_COLUMNS,
                gap: designPx(10),
            } }, documents.map(function (doc) { return (React.createElement(Stack, { key: doc.label, direction: "row", alignItems: "center", onClick: doc.missing ? undefined : doc.onOpen, role: doc.onOpen && !doc.missing ? 'button' : undefined, tabIndex: doc.onOpen && !doc.missing ? 0 : undefined, onKeyDown: doc.onOpen && !doc.missing ? function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    doc.onOpen();
                }
            } : undefined, sx: {
                minWidth: 0,
                gap: designPx(12),
                px: designPx(12),
                py: designPx(10),
                borderRadius: designPx(8),
                border: "1px solid ".concat(REVIEW_COLORS.gray200),
                backgroundColor: 'rgba(249, 250, 251, 0.7)',
                cursor: doc.onOpen && !doc.missing ? 'pointer' : 'default',
                '&:focus-visible': { outline: "2px solid ".concat(REVIEW_COLORS.gray800), outlineOffset: 1 },
            } },
            React.createElement(SquareIcon, { size: 36, radius: 8, iconSize: 18, tinted: true },
                React.createElement(SourceDocumentIcon, { label: doc.label })),
            React.createElement(Box, { sx: { flex: 1, minWidth: 0, pr: designPx(4) } },
                React.createElement(Typography, { sx: OVERLINE_SX }, doc.label),
                doc.missing ? (React.createElement(Typography, { sx: { fontSize: designPx(12), lineHeight: designPx(16), fontWeight: 500, color: REVIEW_COLORS.gray800 } }, "--")) : (React.createElement(Tooltip, { title: doc.fileName, placement: "top", arrow: true, disableInteractive: true },
                    React.createElement(Typography, { noWrap: true, sx: { fontSize: designPx(12), lineHeight: designPx(16), fontWeight: 500, color: REVIEW_COLORS.gray800 } }, doc.fileName))),
                !doc.missing && indicators
                    .filter(function (indicator) { return indicator.documentLabel === doc.label; })
                    .map(function (indicator) { return React.createElement(IndicatorLine, { key: "".concat(indicator.documentLabel, "-").concat(indicator.title), indicator: indicator }); })),
            doc.missing && React.createElement(MissingIcon, { title: "Document Not Found" }))); })),
        unplacedIndicators.length > 0 && (React.createElement(Stack, { direction: "row", flexWrap: "wrap", sx: { columnGap: designPx(16), rowGap: designPx(4), mt: designPx(10) } }, unplacedIndicators.map(function (indicator) { return React.createElement(IndicatorLine, { key: "".concat(indicator.documentLabel, "-").concat(indicator.title), indicator: indicator }); })))));
};
export default OverviewSourceDocuments;
